import React, { useState, useEffect, useMemo } from 'react';
import * as api from '../api/endpoints';

const describe = (e) => {
  if (e.type === 'comment') {
    if (e.visibility === 'internal') return { label: 'Internal note', tone: 'internal' };
    return e.direction === 'customer'
      ? { label: 'Customer replied', tone: 'customer' }
      : { label: 'Reply to customer', tone: 'staff' };
  }
  if (e.type === 'email_in') return { label: 'Email received', tone: 'customer' };
  if (e.type === 'email_out') return { label: 'Email sent', tone: 'staff' };
  return { label: 'Ticket created', tone: 'created' };
};

const Snippet = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 260;
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', marginTop: '0.15rem' }}>
      {open || !long ? text : `${text.slice(0, 260)}…`}
      {long && (
        <button type="button" className="chp-linkbtn" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
};

/**
 * Customer history: all tickets plus one merged timeline (ticket comments, emails).
 * mode="ticket": collapsible panel inside the ticket window.
 * mode="create": compact warning shown while creating a ticket for a customer
 * who already has open tickets.
 */
const CustomerHistoryPanel = ({ customerId, currentTicketId = null, mode = 'ticket' }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [onlyThis, setOnlyThis] = useState(false);
  const [showInternal, setShowInternal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    api.getCustomerHistory(customerId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Could not load customer history.'); });
    return () => { cancelled = true; };
  }, [customerId]);

  const others = useMemo(
    () => (data ? data.tickets.filter((t) => t.id !== currentTicketId) : []),
    [data, currentTicketId],
  );
  const openOthers = others.filter((t) => !t.is_completed);

  const events = useMemo(() => {
    if (!data) return [];
    return data.events
      .filter((e) => (showInternal ? true : !(e.type === 'comment' && e.visibility === 'internal')))
      .filter((e) => (onlyThis && currentTicketId ? e.ticket_id === currentTicketId : true))
      .slice()
      .reverse();
  }, [data, showInternal, onlyThis, currentTicketId]);

  if (error) return <p className="empty-state" style={{ padding: '0.5rem 0' }}>{error}</p>;
  if (!data) return null;

  if (mode === 'create') {
    if (openOthers.length === 0) return null;
    return (
      <div className="chp-warn" role="status">
        <strong>{data.customer.name} already has {openOthers.length} open ticket{openOthers.length > 1 ? 's' : ''}.</strong>
        {' '}Check whether this belongs on one of them instead of creating a new ticket:
        <ul style={{ margin: '0.35rem 0 0 1.1rem', padding: 0 }}>
          {openOthers.slice(0, 5).map((t) => (
            <li key={t.id}>{t.key}: {t.title} <span className="label-chip">{t.status}</span></li>
          ))}
        </ul>
      </div>
    );
  }

  const total = data.tickets.length;
  const summary = `Customer history: ${total} ticket${total === 1 ? '' : 's'}${data.open_ticket_count ? ` (${data.open_ticket_count} open)` : ''}`;

  return (
    <div className="form-group" style={{ marginTop: '0.25rem' }}>
      <button
        type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="chp-body"
        className={`chp-toggle${openOthers.length ? ' chp-attention' : ''}`}
      >
        {open ? '▾' : '▸'} {summary}
        {openOthers.length > 0 && <span> · {openOthers.length} other open</span>}
      </button>

      {open && (
        <div id="chp-body" className="chp-body">
          {data.accounts.length > 1 && (
            <p className="chp-muted" style={{ margin: '0 0 0.5rem' }}>
              Combined from {data.accounts.length} accounts sharing the email {data.customer.email}.
            </p>
          )}

          <h4 style={{ margin: '0 0 0.25rem', fontSize: '0.95rem' }}>Tickets</h4>
          {total === 0 && <p className="empty-state" style={{ padding: '0.25rem 0' }}>No tickets yet.</p>}
          {data.tickets.map((t) => (
            <div key={t.id} style={{ fontSize: '0.9rem', padding: '0.15rem 0' }}>
              <strong>{t.key}</strong> {t.title}{' '}
              <span className="label-chip">{t.status}</span>
              {t.id === currentTicketId && <span className="chp-muted"> (this ticket)</span>}
              <span className="chp-muted"> · {new Date(t.created_at).toLocaleDateString()}</span>
            </div>
          ))}

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '0.75rem 0 0.35rem', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Conversation timeline</h4>
            {currentTicketId && (
              <label style={{ fontSize: '0.85rem' }}>
                <input type="checkbox" checked={onlyThis} onChange={(e) => setOnlyThis(e.target.checked)} /> this ticket only
              </label>
            )}
            <label style={{ fontSize: '0.85rem' }}>
              <input type="checkbox" checked={showInternal} onChange={(e) => setShowInternal(e.target.checked)} /> show internal notes
            </label>
          </div>
          {!data.emails_included && (
            <p className="chp-muted" style={{ margin: '0 0 0.35rem' }}>
              Emails that are not on a ticket are visible to admins only.
            </p>
          )}

          <div style={{ maxHeight: 340, overflowY: 'auto' }} tabIndex={0} role="region" aria-label="Conversation timeline">
            {events.length === 0 && <p className="empty-state" style={{ padding: '0.25rem 0' }}>Nothing to show.</p>}
            {events.map((e, i) => {
              const d = describe(e);
              return (
                <div key={`${e.type}-${e.at}-${i}`} className="chp-event" data-tone={d.tone}>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong className="chp-label">{d.label}</strong>
                    {e.ticket_key && <span className="label-chip" style={{ marginLeft: '0.4rem' }}>{e.ticket_key}</span>}
                    {e.author && <span> · {e.author}</span>}
                    <span className="chp-muted"> · {new Date(e.at).toLocaleString()}</span>
                  </div>
                  {(e.subject || e.title) && <div style={{ fontSize: '0.9rem' }}>{e.subject || e.title}</div>}
                  <Snippet text={e.body} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerHistoryPanel;
